import { LegalShell } from "@/components/app/LegalShell";

const SECTION_H = "text-lg font-display font-semibold tracking-tight mt-8 mb-2";

export default function PrivacyPolicyPage() {
    return (
        <LegalShell
            overline="Legal"
            title="Privacy Policy"
            lastUpdated="February 2026"
            testid="privacy-page"
        >
            <p>
                FormFlo (&ldquo;FormFlo&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is operated by
                <strong> Rishmathi Tech Ventures Pvt Ltd</strong>, with its registered office in Hyderabad, Telangana, India.
                This Privacy Policy explains what information we collect, how we use it, who we share it with, and the rights you have over it.
                By using FormFlo you agree to this policy.
            </p>

            <h2 className={SECTION_H}>1. Information we collect</h2>
            <ul className="list-disc pl-5 space-y-1.5">
                <li><strong>Account data</strong> — your name, work email, organization name, hashed password and role.</li>
                <li><strong>Usage data</strong> — form metadata you build, records you submit, workflow approvals, audit logs, IP addresses and timestamps.</li>
                <li><strong>Billing data</strong> — order details and payment status. Card / UPI / bank details are processed only by Zoho Payments; FormFlo never sees or stores them.</li>
                <li><strong>Cookies</strong> — strictly an httpOnly session cookie issued at login. No third-party tracking or advertising cookies.</li>
            </ul>

            <h2 className={SECTION_H}>2. How we use your information</h2>
            <ul className="list-disc pl-5 space-y-1.5">
                <li>To provide and operate the FormFlo service for your organization.</li>
                <li>To authenticate users, enforce RBAC and prevent abuse / brute-force attempts.</li>
                <li>To send transactional emails (invitations, password resets, approval notifications, billing receipts).</li>
                <li>To improve product reliability and add features. We do not sell your data — ever.</li>
            </ul>

            <h2 className={SECTION_H}>3. Data you must not store</h2>
            <p>
                FormFlo is a general-purpose low-code platform. <strong>You must not store sensitive information</strong>{" "}
                inside form fields or records, including but not limited to:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
                <li>Passwords, PINs, OTPs, security questions or any authentication secret;</li>
                <li>Full credit / debit card numbers, CVV, expiry, UPI PIN or net-banking credentials;</li>
                <li>Aadhaar, PAN, passport or any government ID numbers (unless explicitly required by a regulated workflow that you have separately compliance-certified);</li>
                <li>Medical records, biometric identifiers, or any data restricted by Indian DPDP Act / GDPR &ldquo;special categories&rdquo;.</li>
            </ul>
            <p>
                If we detect such data we may quarantine the relevant records and notify you. Repeated violations may result in suspension under our Terms.
            </p>

            <h2 className={SECTION_H}>4. How we share data</h2>
            <p>
                We share only what is strictly required to run the service:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
                <li><strong>MongoDB Atlas</strong> — encrypted at rest, used to store your tenant data.</li>
                <li><strong>Zoho Payments</strong> — payment processing (PCI-DSS compliant).</li>
                <li><strong>Zoho ZeptoMail</strong> — transactional email delivery (only for messages you trigger).</li>
                <li><strong>Hosting infrastructure</strong> — cloud-based, with TLS for all traffic.</li>
            </ul>
            <p>We do not transfer your data to any party for advertising or analytics.</p>

            <h2 className={SECTION_H}>5. Multi-tenancy &amp; isolation</h2>
            <p>
                Every record, file and configuration is scoped to a single <em>organization (tenant)</em> via a strict
                <code className="font-mono text-xs"> org_id </code>filter. Users from one tenant can never read or write data of another tenant.
            </p>

            <h2 className={SECTION_H}>6. Retention</h2>
            <p>
                We retain customer data for as long as your organization remains an active subscriber. On cancellation we retain
                data for 30 days (to allow re-activation) and then permanently delete it on request. Audit logs may be kept for up
                to 12 months to fulfil security / compliance obligations.
            </p>

            <h2 className={SECTION_H}>7. Your rights</h2>
            <p>
                Subject to the Digital Personal Data Protection Act, 2023 (India), you may request access, correction, deletion
                or export of your personal data. Org Admins can fulfil most of these requests directly through the FormFlo UI;
                for anything else, contact us at <a href="mailto:contact@rishmathi.com" className="text-primary hover:underline">contact@rishmathi.com</a>.
            </p>

            <h2 className={SECTION_H}>8. Security</h2>
            <ul className="list-disc pl-5 space-y-1.5">
                <li>Passwords hashed with bcrypt; JWTs signed with HMAC-SHA256.</li>
                <li>API keys (ZeptoMail, Zoho Payments) are encrypted with a server-side secret before storage.</li>
                <li>TLS 1.2+ enforced for all client–server traffic.</li>
                <li>Login attempts are rate-limited per email and IP.</li>
            </ul>
            <p>No system is 100% secure — please report vulnerabilities to <a href="mailto:contact@rishmathi.com" className="text-primary hover:underline">contact@rishmathi.com</a> responsibly.</p>

            <h2 className={SECTION_H}>9. Changes to this policy</h2>
            <p>
                We may update this policy from time to time. We will notify Org Admins by email on any material change.
                Continued use of FormFlo after the change constitutes acceptance.
            </p>

            <h2 className={SECTION_H}>10. Contact</h2>
            <p>
                Rishmathi Tech Ventures Pvt Ltd · Hyderabad, Telangana, India ·{" "}
                <a href="mailto:contact@rishmathi.com" className="text-primary hover:underline">contact@rishmathi.com</a>
            </p>
        </LegalShell>
    );
}
